import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import TimeDatePickerModal from '../common/TimeDatePickerModal';
import './FreelancerDashboard.css';

// Helper functions for API base URL and time handling
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

// Helper function to format time as HH:MM from free text
const formatTimeInput = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length > 4) return value;
  if (digits.length <= 2) {
    return digits;
  }
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
};

// Validate 24-hour HH:MM
const validateTime = (time) => {
  if (!time) return false;
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};

export default function FreelancerDashboard() {
  const { currentUser } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    upcoming_assignments: [],
    total_earnings_all_time: 0,
    earnings_this_month: 0
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [calendar, setCalendar] = useState({ month: calendarMonth, days: {} });
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [availabilityRanges, setAvailabilityRanges] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAvailabilityPicker, setShowAvailabilityPicker] = useState(false);
  const [editingRangeIndex, setEditingRangeIndex] = useState(null);
  const [profileData, setProfileData] = useState(null);

  const addAvailabilityRange = (range) => {
    if (editingRangeIndex !== null) {
      setAvailabilityRanges((prev) => {
        const updated = [...prev];
        updated[editingRangeIndex] = range;
        return updated;
      });
      setEditingRangeIndex(null);
    } else {
      setAvailabilityRanges((prev) => [...prev, range]);
    }
    setShowAvailabilityPicker(false);
  };

  const removeAvailabilityRange = (index) => {
    setAvailabilityRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const editAvailabilityRange = (index) => {
    setEditingRangeIndex(index);
    setShowAvailabilityPicker(true);
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchDashboard = async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/api/freelancer/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!res.ok) {
          throw new Error('Failed to load dashboard');
        }
        const json = await res.json();
        setData(json || {});
      } catch (e) {
        console.error('Failed to load freelancer dashboard', e);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [currentUser, navigate]);

  // Check if user has admin role via custom claims
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      try {
        let tr = await currentUser.getIdTokenResult();
        const hasAdminFromClaims = (() => {
          const claims = tr?.claims || {};
          const roles = claims.roles;
          if (Array.isArray(roles)) {
            return roles.map(String).map((r) => r.toLowerCase()).includes('admin');
          }
          if (typeof roles === 'string') {
            return roles.toLowerCase() === 'admin';
          }
          return !!claims.admin;
        })();
        if (!hasAdminFromClaims) {
          // Force refresh in case claims were just updated
          tr = await currentUser.getIdTokenResult(true);
          const claims = tr?.claims || {};
          const roles = claims.roles;
          const refreshedAdmin =
            (Array.isArray(roles) && roles.map(String).map((r) => r.toLowerCase()).includes('admin')) ||
            (typeof roles === 'string' && roles.toLowerCase() === 'admin') ||
            !!claims.admin;
          setIsAdmin(refreshedAdmin);
        } else {
          setIsAdmin(hasAdminFromClaims);
        }
      } catch (e) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser]);

  // Load freelancer availability and profile from profile
  useEffect(() => {
    const loadAvailability = async () => {
      if (!currentUser) return;
      try {
        setAvailabilityLoading(true);
        const token = await currentUser.getIdToken();
        let res;
        try {
          res = await fetch(`${API_BASE_URL}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch {
          res = await fetch(`${API_BASE_URL}/api/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        if (res.ok) {
          const json = await res.json();
          if (json) {
            // Store profile data for email/displayName fallback
            setProfileData(json);
            // Support both old weekly format and new monthly format
            if (json.availabilityRanges && Array.isArray(json.availabilityRanges)) {
              setAvailabilityRanges(json.availabilityRanges);
            } else if (json.availability) {
              // Old weekly format - convert to empty array for now
              setAvailabilityRanges([]);
            } else {
              setAvailabilityRanges([]);
            }
          }
        }
      } catch (e) {
        console.error('Failed to load freelancer availability', e);
      } finally {
        setAvailabilityLoading(false);
      }
    };

    loadAvailability();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const loadCalendar = async () => {
      try {
        setCalendarLoading(true);
        const token = await currentUser.getIdToken();
        const res = await fetch(
          `${API_BASE_URL}/api/freelancer/calendar?month=${encodeURIComponent(
            calendarMonth
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        if (!res.ok) {
          throw new Error('Failed to load calendar');
        }
        const json = await res.json();
        setCalendar({
          month: json.month || calendarMonth,
          days: json.days || {}
        });
      } catch (e) {
        console.error('Failed to load freelancer calendar', e);
      } finally {
        setCalendarLoading(false);
      }
    };

    loadCalendar();
  }, [currentUser, calendarMonth]);

  // Try to reload user if email is missing (will work after backend fix is deployed)
  useEffect(() => {
    if (currentUser && !currentUser.email && currentUser.reload) {
      currentUser.reload().catch(err => {
        console.warn('Failed to reload user:', err);
      });
    }
  }, [currentUser]);

  // Get email from profile data as fallback if currentUser.email is null
  const userEmail = currentUser?.email || profileData?.email || data?.email || '';
  const displayName =
    (data && data.displayName) ||
    profileData?.displayName ||
    currentUser?.displayName ||
    (userEmail && userEmail.split('@')[0]) ||
    '';

  if (!currentUser) {
    return null;
  }


  const changeMonth = (delta) => {
    setCalendarMonth((prev) => {
      const [yearStr, monthStr] = prev.split('-');
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10) - 1; // JS month
      const next = new Date(year, month + delta, 1);
      return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
    });
  };

  const calendarEntries = Object.entries(calendar.days || {}).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  const formatMonthLabel = (ym) => {
    const [year, month] = ym.split('-').map((v) => parseInt(v, 10));
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric'
    });
  };

  const saveAvailability = async () => {
    if (!currentUser || savingAvailability) return;
    setSavingAvailability(true);
    try {
      const token = await currentUser.getIdToken();
      await fetch(`${API_BASE_URL}/api/user/profile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          availabilityRanges,
          email: userEmail || currentUser.email
        })
      });
      // alert(t('common.save'));
    } catch (e) {
      console.error('Failed to save availability', e);
      alert(t('dashboard.failedToUpdateVisibility') || 'Failed to save availability');
    } finally {
      setSavingAvailability(false);
    }
  };

  if (loading) {
    return (
      <div className="freelancer-dashboard-container">
        <div className="freelancer-dashboard-loading">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`freelancer-dashboard-container ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="freelancer-dashboard-header">
        <div className="freelancer-dashboard-header-content">
          <div className="freelancer-dashboard-header-left">
          </div>
          <div className="freelancer-dashboard-title">
            <h1>{t('freelancerDashboard.title') || 'Freelancer Dashboard'}</h1>
            <p className="freelancer-dashboard-subtitle">
              {t('freelancerDashboard.subtitle') ||
                'See your upcoming work, history and earnings in one place.'}
            </p>
          </div>
        </div>
      </header>

      <main className="freelancer-dashboard-main">
        <section className="freelancer-dashboard-hero">
            <h2 className="freelancer-dashboard-greeting">
            {t('freelancerDashboard.greetingPrefix') || 'Hi'}{' '}
            <span className="freelancer-dashboard-greeting-name">
              {displayName || userEmail || 'User'}
            </span>
          </h2>
          <div className="freelancer-dashboard-hero-stats">
            <div className="freelancer-dashboard-hero-card">
              <div className="hero-card-label">
                {t('freelancerDashboard.earningsThisMonth') || "This month's earnings"}
              </div>
              <div className="hero-card-value">
                ₪{Number(data.earnings_this_month || 0).toLocaleString()}
              </div>
            </div>
            <div className="freelancer-dashboard-hero-card">
              <div className="hero-card-label">
                {t('freelancerDashboard.totalEarnings') || 'Total earnings'}
              </div>
              <div className="hero-card-value">
                ₪{Number(data.total_earnings_all_time || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </section>

        <section className="freelancer-dashboard-actions">
          <button
            className="freelancer-dashboard-action-button primary"
            onClick={() => navigate('/jobs')}
          >
            {t('freelancerDashboard.searchJobs') || 'Search for jobs'}
          </button>
          <button
            className="freelancer-dashboard-action-button"
            onClick={() => navigate('/freelancer/earnings')}
          >
            {t('freelancerDashboard.myEarnings') || 'My earnings'}
          </button>
        </section>

        <section className="freelancer-dashboard-calendar">
          <div className="freelancer-dashboard-calendar-header">
            <h3>{t('freelancerDashboard.calendarTitle') || 'Upcoming work this month'}</h3>
            <div className="freelancer-dashboard-calendar-nav">
              <button
                className="freelancer-dashboard-calendar-nav-btn"
                onClick={() => changeMonth(-1)}
              >
                ‹
              </button>
              <span className="freelancer-dashboard-calendar-month">
                {formatMonthLabel(calendar.month)}
              </span>
              <button
                className="freelancer-dashboard-calendar-nav-btn"
                onClick={() => changeMonth(1)}
              >
                ›
              </button>
            </div>
          </div>
          {calendarLoading ? (
            <p className="freelancer-dashboard-empty">
              {t('common.loading')}
            </p>
          ) : calendarEntries.length > 0 ? (
            <ul className="freelancer-dashboard-calendar-list">
              {calendarEntries.map(([day, assignments]) => (
                <li key={day} className="freelancer-dashboard-calendar-item">
                  <div className="calendar-item-date">{day}</div>
                  <div className="calendar-item-body">
                    {assignments.map((a) => (
                      <div
                        key={a.id}
                        className="calendar-item-row"
                        onClick={() => navigate(`/job/${a.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="calendar-item-title">
                          {(a.job_title && String(a.job_title).trim()) ||
                            t('freelancerDashboard.assignment')}
                        </div>
                        <div className="calendar-item-meta">
                          {(a.start_time || a.end_time) && (
                            <span>
                              {a.start_time} - {a.end_time}
                            </span>
                          )}
                          {a.pay_amount != null && (
                            <span>₪{Number(a.pay_amount).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="freelancer-dashboard-empty">
              {t('freelancerDashboard.noAssignments') ||
                'No planned work days this month yet.'}
            </p>
          )}
        </section>

        <section className="freelancer-dashboard-availability">
          <h3>{t('dashboard.availabilityFormTitle') || 'Monthly Availability'}</h3>
          <p className="freelancer-dashboard-availability-hint">
            {t('dashboard.availabilityFormDescription') ||
              'Set your available date ranges and times. Select a date range and set times once for all days in that range.'}
          </p>
          {availabilityLoading ? (
            <p className="freelancer-dashboard-empty">
              {t('common.loading')}
            </p>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setEditingRangeIndex(null);
                    setShowAvailabilityPicker(true);
                  }}
                  className="freelancer-dashboard-action-button primary"
                  style={{ marginBottom: '1rem' }}
                >
                  {t('dashboard.addAvailabilityRange') || '+ Add Availability Range'}
                </button>
              </div>
              {availabilityRanges.length === 0 ? (
                <p className="freelancer-dashboard-empty" style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('dashboard.noAvailabilityRanges') || 'No availability ranges set. Click the button above to add one.'}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {availabilityRanges.map((range, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '1rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500', marginBottom: '0.25rem' }}>
                            {new Date(range.startDate).toLocaleDateString()} - {new Date(range.endDate).toLocaleDateString()}
                          </div>
                          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
                            {range.startTime} - {range.endTime}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            onClick={() => editAvailabilityRange(index)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(59, 130, 246, 0.2)',
                              border: '1px solid rgba(59, 130, 246, 0.4)',
                              borderRadius: '6px',
                              color: 'rgba(255, 255, 255, 0.9)',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {t('common.edit') || 'Edit'}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAvailabilityRange(index)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '6px',
                              color: 'rgba(255, 255, 255, 0.9)',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            {t('common.delete') || 'Delete'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="freelancer-dashboard-availability-actions">
            <button
              className="freelancer-dashboard-action-button primary"
              onClick={saveAvailability}
              disabled={savingAvailability}
            >
              {savingAvailability ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </section>
      </main>

      {showAvailabilityPicker && (
        <TimeDatePickerModal
          isOpen={showAvailabilityPicker}
          onClose={() => {
            setShowAvailabilityPicker(false);
            setEditingRangeIndex(null);
          }}
          onConfirm={(result) => {
            if (result.date && result.endDate && result.time && result.endTime) {
              addAvailabilityRange({
                startDate: result.date,
                endDate: result.endDate,
                startTime: result.time,
                endTime: result.endTime
              });
            }
          }}
          mode="datetimerange"
          initialDate={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.startDate : null}
          initialEndDate={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.endDate : null}
          initialTime={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.startTime : null}
          initialEndTime={editingRangeIndex !== null ? availabilityRanges[editingRangeIndex]?.endTime : null}
          title={t('dashboard.selectAvailabilityRange') || 'Select Date Range & Times'}
        />
      )}
    </div>
  );
}


